import React, { useEffect } from "react";
import {
  FaTimes, FaUser, FaLayerGroup, FaInfoCircle,
  FaClock, FaUsers, FaShieldAlt,
} from "react-icons/fa";
import { useGetProjectDetails } from "../../../hooks/API/projects.api";
import { formatDateTime } from "../../../utils/formatTimestamp";
import { ProjectStatusBadge, ProjectTypeBadge } from "../../common";

const ViewModal = ({ selectedProject, onClose }) => {
  const { data: projectDetails, isLoading, refetch, isFetching } = useGetProjectDetails(selectedProject?.id);

   useEffect(()=>{
        if (selectedProject) {
      // Fetch project details when the modal opens
      refetch();
    } 
   },[selectedProject, refetch]);

  const project = projectDetails?.project;

  const passRate =
    project?.total_cases > 0
      ? Math.round((project.total_passed / project.total_cases) * 100)
      : 0;

  return (
    <div data-rt-modal-overlay className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl h-[80vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-[fadeIn_.2s_ease]"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-6 py-5 bg-gradient-to-r from-indigo-500 to-blue-500 text-white">
          <div className="min-w-0 max-w-[75%]">
            <h2
              className="text-xl font-semibold truncate"
              title={
                isLoading
                  ? undefined
                  : project?.project_name ?? selectedProject?.name
              }
            >
              {isLoading ? (
                <div className="h-5 w-40 bg-white/30 rounded animate-pulse" />
              ) : (
                project?.project_name ?? selectedProject.name
              )}
            </h2>
            <p className="text-sm opacity-80">Project overview</p>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 p-2 rounded-lg hover:bg-white/20 transition"
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {(isLoading || isFetching) ? (
            <SkeletonBody />
          ) : project ? (
            <ProjectBody project={project} passRate={passRate} />
          ) : (
            <p className="text-sm text-gray-500 text-center py-10">
              Failed to load project details.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-medium bg-gray-200 hover:bg-gray-300 rounded-lg transition"
          >
            Close
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
      `}</style>
    </div>
  );
};


const Skel = ({ className = "" }) => (
  <div className={`bg-gray-200 rounded animate-pulse ${className}`} />
);

const SkeletonBody = () => (
  <div className="space-y-6">
    <div>
      <Skel className="h-3 w-24 mb-3" />
      <Skel className="h-16 w-full" />
    </div>
    <div className="grid grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-gray-50 p-4 rounded-xl text-center space-y-2">
          <Skel className="h-2 w-1/2 mx-auto" />
          <Skel className="h-5 w-1/3 mx-auto" />
        </div>
      ))}
    </div>
    <div className="grid grid-cols-2 gap-6">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="space-y-2">
          <Skel className="h-2 w-16" />
          <Skel className="h-4 w-28" />
        </div>
      ))}
    </div>
    <div>
      <Skel className="h-3 w-20 mb-3" />
      <div className="flex gap-2">
        {[...Array(2)].map((_, i) => (
          <Skel key={i} className="h-8 w-8 rounded-full" />
        ))}
      </div>
    </div>
  </div>
);


const ProjectBody = ({ project, passRate }) => (
  <div className="space-y-6">
    {/* Description */}
    <div>
      <Label icon={<FaInfoCircle />}>Description</Label>
      <p className="text-sm text-gray-700 bg-gray-50 p-4 rounded-xl leading-relaxed">
        {project.description || "No description provided"}
      </p>
    </div>

    {/* Stats */}
    <div className="grid grid-cols-4 gap-4">
      <StatCard label="Total cases" value={project.total_cases} />
      <StatCard label="Passed" value={project.total_passed} valueClass="text-green-600" />
      <StatCard label="Failed" value={project.total_failed} valueClass="text-red-500" />
      <StatCard label="Pass rate" value={`${passRate}%`} valueClass="text-green-600" />
    </div>

    {/* Meta */}
    <div className="grid grid-cols-2 gap-6">
      <div className="flex flex-col gap-2">
        <Label icon={<FaLayerGroup />}>Type</Label>
        <ProjectTypeBadge type={project.project_type} />
      </div>

      <div className="flex flex-col gap-2">
        <Label icon={<FaShieldAlt />}>Status</Label>
        <ProjectStatusBadge status={project.status} />
      </div>

      <div className="flex flex-col gap-1">
        <Label icon={<FaUser />}>Created by</Label>
        <span className="text-sm font-medium text-gray-800">
          {project.created_by.name}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <Label icon={<FaUsers />}>Total members</Label>
        <span className="text-sm font-medium text-gray-800">
          {project.total_members}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <Label icon={<FaClock />}>Created at</Label>
        <span className="text-sm text-gray-700">
          {formatDateTime(project.created_at) || 'N/A'}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <Label icon={<FaClock />}>Updated at</Label>
        <span className="text-sm text-gray-700">
          {formatDateTime(project.updated_at) || 'N/A'}
        </span>
      </div>
    </div>

    {/* Members */}
    {project.members?.length > 0 && (
      <div>
        <Label icon={<FaUsers />}>Members</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {project.members.map((m) => (
            <div
              key={m.user_id}
              title={`${m.first_name} ${m.last_name}`}
              className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 rounded-full text-xs font-medium text-indigo-700"
            >
              <div className="w-5 h-5 rounded-full bg-indigo-200 flex items-center justify-center text-[10px] font-bold text-indigo-700">
                {m.first_name[0]}{m.last_name[0]}
              </div>
              {m.first_name} {m.last_name}
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);


const Label = ({ icon, children }) => (
  <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-1">
    {icon} {children}
  </div>
);

const StatCard = ({ label, value, valueClass = "text-gray-800" }) => (
  <div className="bg-gray-50 p-4 rounded-xl text-center">
    <p className="text-xs text-gray-400">{label}</p>
    <p className={`text-lg font-semibold mt-0.5 ${valueClass}`}>{value}</p>
  </div>
);

export default ViewModal;